
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  ArrowUpRight, 
  ArrowDownRight, 
  TrendingUp, 
  Download, 
  Calendar, 
  RefreshCw,
  Users,
  Share2,
  Zap,
  MessagesSquare,
  ThumbsUp,
  Eye,
  BarChart as BarChartIcon,
  ImageIcon,
  VideoIcon,
  MessageSquare
} from 'lucide-react';

// Sample data for campaign performance
const performanceData = [
  { date: 'Week 1', reach: 5400, engagement: 320, shares: 48, clicks: 210 },
  { date: 'Week 2', reach: 8700, engagement: 540, shares: 82, clicks: 410 },
  { date: 'Week 3', reach: 12500, engagement: 860, shares: 124, clicks: 640 },
  { date: 'Week 4', reach: 15200, engagement: 970, shares: 156, clicks: 820 },
  { date: 'Week 5', reach: 18400, engagement: 1240, shares: 218, clicks: 1050 },
  { date: 'Week 6', reach: 24600, engagement: 1680, shares: 276, clicks: 1380 },
];

// Sample data for platform breakdown
const platformData = [
  { name: 'Instagram', value: 42, color: '#E1306C' },
  { name: 'TikTok', value: 28, color: '#25F4EE' },
  { name: 'Spotify', value: 18, color: '#1DB954' },
  { name: 'YouTube', value: 12, color: '#FF0000' },
];

// Sample data for demographic breakdown
const demographicData = [
  { name: '18-24', value: 35, color: '#8B5CF6' },
  { name: '25-34', value: 40, color: '#6366F1' },
  { name: '35-44', value: 15, color: '#8B5CF6' },
  { name: '45+', value: 10, color: '#C4B5FD' },
];

// Sample data for content performance
const contentPerformanceData = [
  { 
    name: 'Teaser Video', 
    type: 'video',
    reach: 12400, 
    engagement: 1840, 
    shares: 245,
    engagementRate: 14.8,
    platform: 'Instagram'
  },
  { 
    name: 'Album Artwork', 
    type: 'image',
    reach: 9800, 
    engagement: 1260, 
    shares: 186,
    engagementRate: 12.9,
    platform: 'Instagram'
  },
  { 
    name: 'Behind the Scenes', 
    type: 'video',
    reach: 8200, 
    engagement: 980, 
    shares: 124,
    engagementRate: 12.0,
    platform: 'TikTok'
  },
  { 
    name: 'Release Announcement', 
    type: 'post',
    reach: 15600, 
    engagement: 2240, 
    shares: 312,
    engagementRate: 14.4,
    platform: 'All Platforms'
  },
  { 
    name: 'Artist Interview', 
    type: 'video',
    reach: 7200, 
    engagement: 860, 
    shares: 95,
    engagementRate: 11.9,
    platform: 'YouTube'
  },
];

// KPI cards data
const kpiData = [
  { 
    title: 'Total Reach', 
    value: '24.6K', 
    change: '+32%', 
    trend: 'up',
    icon: <Eye className="h-4 w-4" />
  },
  { 
    title: 'Engagement Rate', 
    value: '6.8%', 
    change: '+1.2%', 
    trend: 'up',
    icon: <ThumbsUp className="h-4 w-4" />
  },
  { 
    title: 'Conversion Rate', 
    value: '3.2%', 
    change: '-0.5%', 
    trend: 'down',
    icon: <Zap className="h-4 w-4" />
  },
  { 
    title: 'Avg. Response Time', 
    value: '24min', 
    change: '-8min', 
    trend: 'up',
    icon: <MessagesSquare className="h-4 w-4" />
  },
];

const CampaignAnalytics: React.FC = () => {
  const [selectedCampaign, setSelectedCampaign] = useState('campaign1');
  const [dateRange, setDateRange] = useState('all');
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Campaign Analytics</h2>
          <p className="text-muted-foreground">Track and analyze your campaign performance</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select campaign" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="campaign1">Summer Album Release</SelectItem>
              <SelectItem value="campaign2">US Tour Promotion</SelectItem>
              <SelectItem value="campaign3">New Music Video</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
              <SelectItem value="quarter">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" title="Refresh data">
            <RefreshCw className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {kpiData.map((kpi, index) => (
          <Card key={index} className="bg-card/40 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-muted-foreground">
                  {kpi.icon}
                  <span>{kpi.title}</span>
                </div>
                <Badge 
                  variant="outline" 
                  className={`
                    ${kpi.trend === 'up' 
                      ? 'bg-green-500/10 text-green-500 border-green-500/20' 
                      : 'bg-red-500/10 text-red-500 border-red-500/20'
                    }
                  `}
                >
                  <span className="flex items-center">
                    {kpi.trend === 'up' ? (
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                    ) : (
                      <ArrowDownRight className="h-3 w-3 mr-1" />
                    )}
                    {kpi.change}
                  </span>
                </Badge>
              </div>
              <div className="mt-2 text-3xl font-bold">{kpi.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Performance Charts */}
      <Card>
        <CardHeader>
          <CardTitle>Campaign Performance</CardTitle>
          <CardDescription>Key metrics over time</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview">
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="reach">Reach</TabsTrigger>
              <TabsTrigger value="engagement">Engagement</TabsTrigger>
              <TabsTrigger value="conversion">Conversion</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={performanceData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#222', borderColor: '#444' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="reach" 
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }} 
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="engagement" 
                      stroke="#82ca9d" 
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="shares" 
                      stroke="#ffc658" 
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="reach">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={performanceData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#222', borderColor: '#444' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Bar dataKey="reach" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="engagement">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={performanceData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#222', borderColor: '#444' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Bar dataKey="engagement" fill="#82ca9d" />
                    <Bar dataKey="shares" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="conversion">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={performanceData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#222', borderColor: '#444' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="clicks" 
                      stroke="#ff7300" 
                      activeDot={{ r: 8 }} 
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Platform & Demographics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Platform Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Platform Breakdown</CardTitle>
            <CardDescription>Performance across different platforms</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={platformData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {platformData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#222', borderColor: '#444' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Demographic Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Audience Demographics</CardTitle>
            <CardDescription>Age distribution of your audience</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={demographicData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" stroke="#888" />
                  <YAxis stroke="#888" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#222', borderColor: '#444' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {demographicData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Top Performing Content */}
      <Card>
        <CardHeader>
          <CardTitle>Top Performing Content</CardTitle>
          <CardDescription>Content with the highest engagement rates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-muted">
                  <th className="text-left py-3 px-2">Content</th>
                  <th className="text-left py-3 px-2">Platform</th>
                  <th className="text-right py-3 px-2">Reach</th>
                  <th className="text-right py-3 px-2">Engagement</th>
                  <th className="text-right py-3 px-2">Rate</th>
                  <th className="text-right py-3 px-2">Shares</th>
                </tr>
              </thead>
              <tbody>
                {contentPerformanceData.map((content, index) => (
                  <tr 
                    key={index}
                    className="border-b border-muted last:border-0 hover:bg-muted/50"
                  >
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        {content.type === 'video' ? (
                          <VideoIcon className="h-4 w-4 text-red-500" />
                        ) : content.type === 'image' ? (
                          <ImageIcon className="h-4 w-4 text-blue-500" />
                        ) : (
                          <MessageSquare className="h-4 w-4 text-green-500" />
                        )}
                        <span>{content.name}</span>
                      </div>
                    </td>
                    <td className="py-2 px-2">{content.platform}</td>
                    <td className="py-2 px-2 text-right">{content.reach.toLocaleString()}</td>
                    <td className="py-2 px-2 text-right">{content.engagement.toLocaleString()}</td>
                    <td className="py-2 px-2 text-right">
                      <Badge variant={content.engagementRate > 13 ? 'default' : 'outline'}>
                        {content.engagementRate}%
                      </Badge>
                    </td>
                    <td className="py-2 px-2 text-right">{content.shares}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button variant="link" className="text-sm">View all content performance</Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Action Items */}
      <Card>
        <CardHeader>
          <CardTitle>Insights & Recommendations</CardTitle>
          <CardDescription>AI-powered suggestions to improve your campaign</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-md">
              <div className="font-medium flex items-center gap-2 text-green-500">
                <TrendingUp className="h-4 w-4" />
                Performance Insight
              </div>
              <p className="mt-1 text-sm">
                Your engagement rate has increased 32% since implementing video content. Continue focusing on video-based content to maximize engagement.
              </p>
            </div>
            
            <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-md">
              <div className="font-medium flex items-center gap-2 text-purple-500">
                <Users className="h-4 w-4" />
                Audience Insight
              </div>
              <p className="mt-1 text-sm">
                Your content performs best with the 25-34 age group. Consider tailoring future content to resonate more with this demographic.
              </p>
            </div>
            
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-md">
              <div className="font-medium flex items-center gap-2 text-blue-500">
                <Calendar className="h-4 w-4" />
                Timing Suggestion
              </div>
              <p className="mt-1 text-sm">
                Posts published between 7-9pm receive 28% more engagement. Consider scheduling your next important announcements during this timeframe.
              </p>
            </div>
            
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
              <div className="font-medium flex items-center gap-2 text-amber-500">
                <BarChartIcon className="h-4 w-4" />
                Platform Recommendation
              </div>
              <p className="mt-1 text-sm">
                Instagram is your top-performing platform with 2.8x the engagement rate of other platforms. Prioritize Instagram content while maintaining presence on other platforms.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignAnalytics;
